"use client"

import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import { useInView } from "@/hooks/use-in-view";

interface MenuItemProps {
    imageSrc: string;
    imageAlt: string;
    title: string;
    subtitle: string;
    description: string;
    buttonText: string;
    href: string;
    position: 'left' | 'right';
    inView: boolean;
    ref: React.RefObject<HTMLDivElement | null>;
}

function MenuItem({ imageSrc, imageAlt, title, subtitle, description, buttonText, href, position, inView, ref }: MenuItemProps) {
    const isLeft = position === 'left';
    return (
        <div ref={ref} className="relative py-16 ">
            <div className={`relative w-full md:w-2/3 ${isLeft ? '' : 'md:ml-auto'} h-[550px] md:h-[700px] group animate-on-scroll ${inView ? `animate-fade-in-${isLeft ? 'left' : 'right'} delay-200` : ''}`}>
                <div className="relative w-full h-full transition-transform duration-700 group-hover:scale-105 ">
                    <Image
                        src={imageSrc}
                        alt={imageAlt}
                        fill
                        className="object-cover"
                        priority={position === 'left'} // Only first item gets priority
                        sizes="(max-width: 768px) 100vw, 66vw"
                    />
                    <div className="absolute inset-0 bg-black/20 pointer-events-none" />
                </div>
                {/* Text overlay */}
                <div className={`group absolute z-20 top-1/4 ${isLeft ? 'md:-right-32 lg:-right-40' : 'md:-left-32 lg:-left-40'} w-full md:w-7/12 lg:w-1/2 border border-white/10 bg-white/5 backdrop-blur-md shadow-xl rounded-sm animate-on-scroll ${inView ? `animate-fade-in-${isLeft ? 'right' : 'left'}` : ''}`}>
                    <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-br from-[#a19b92]/20 via-[#222]/10 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300" />
                    <div className={`relative z-10 py-6 p-4 sm:p-6 md:p-12 flex flex-col ${!isLeft ? 'md:items-end' : 'md:items-start'}`}>
                        <h3 className={`absolute -top-1/3 -left-4 md:left-auto text-[2.5rem] md:text-[5rem] font-adobe italic leading-none uppercase tracking-wider mb-4 text-white animate-on-scroll ${inView ? 'animate-fade-in-up delay-200' : ''} ${!isLeft ? 'md:text-right md:right-0' : ''}`}>
                            {title}
                        </h3>
                        <p className={`text-[#bdb8ae] text-justify mb-4 animate-on-scroll ${inView ? 'animate-fade-in-up delay-300' : ''}`}>
                            {subtitle}
                        </p>
                        <p className={`text-[#bdb8ae] text-justify text-sm md:text-base mb-2 animate-on-scroll ${inView ? 'animate-fade-in-up delay-400' : ''}`}>
                            {description}
                        </p>
                        <Button
                            asChild
                            variant="outline"
                            className={`mt-3 rounded-none border-white text-black hover:bg-white hover:text-black animate-on-scroll ${inView ? 'animate-fade-in-up delay-500' : ''}`}
                            aria-label={`${buttonText} für ${title}`}
                        >
                            <Link href={href}>{buttonText}</Link>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Menu() {
    const [titleRef, titleInView] = useInView({ threshold: 0.1 });
    const [item1Ref, item1InView] = useInView({ threshold: 0.3 });
    const [item2Ref, item2InView] = useInView({ threshold: 0.3 });
    const [item3Ref, item3InView] = useInView({ threshold: 0.3 });

    const menuItems = [
        {
            imageSrc: "/images/20.jpg",
            imageAlt: "Fingerfood Buffet für Events in Erlangen - 30+ Jahre Erfahrung",
            title: "Fingerfood Catering",
            subtitle: "Raffinierte Canapés, Snacks und Häppchen für Messen oder private Events. Perfekt für entspannte Geselligkeit und Networking.",
            description: "Unser Fingerfood Catering in Erlangen, Nürnberg und Fürth bietet kreative Canapés und Snacks – ideal für Messen, Firmenfeiern und private Events. Perfekt für Genießer, die Wert auf Qualität und regionale Zutaten legen.",
            buttonText: "Menübeispiele",
            href: "/catering/fingerfood",
            position: 'right' as const,
            ref: item1Ref,
            inView: item1InView
        },
        {
            imageSrc: "/images/21.jpg",
            imageAlt: "Buffet Catering Nürnberg - Professioneller Service seit über 30 Jahren",
            title: "Buffet Catering",
            subtitle: "Vielfältige Buffets für jeden Geschmack – von fränkisch-regional bis international. Perfekt für große und kleine Veranstaltungen.",
            description: "Genießen Sie unser Buffet Catering in Nürnberg, Erlangen und Umgebung: Vielfältige, individuell gestaltete Buffets für Hochzeiten, Firmenfeiern und Events. Frische, biozertifizierte Zutaten und erstklassiger Service.",
            buttonText: "Menübeispiele",
            href: "/catering/buffet",
            position: 'left' as const,
            ref: item2Ref,
            inView: item2InView
        },
        {
            imageSrc: "/images/18.jpg",
            imageAlt: "Menü-Service Erlangen - Elegante Mehrgang-Menüs mit 30+ Jahren Erfahrung",
            title: "Menü-Service",
            subtitle: "Elegante Menüs mit mehreren Gängen, serviert von unserem professionellen Team. Ideal für festliche Anlässe und besondere Momente.",
            description: "Unser Menü-Service für Erlangen, Nürnberg und Fürth verwöhnt Sie mit exklusiven Mehrgang-Menüs – perfekt für Hochzeiten, Gala-Abende und besondere Anlässe. Regionale Küche, kreativ interpretiert.",
            buttonText: "Menübeispiele",
            href: "/catering/flying-menue",
            position: 'right' as const,
            ref: item3Ref,
            inView: item3InView
        }
    ];

    return (
        <section
            id="menu"
            className="py-20 md:py-28 bg-transparent text-white relative"
            aria-labelledby="menu-title"
        >
            <div
                ref={titleRef}
                className={`absolute flex justify-end pointer-events-none overflow-hidden z-0 w-full md:-top-10 animate-on-scroll ${titleInView ? 'animate-fade-in-right' : ''}`}
            >
                <span className="sr-only">
                    Bachmann Catering-Menüs - Über 30 Jahre Erfahrung in Erlangen, Nürnberg und Fürth
                </span>
            </div>

            <div className="container relative z-10">
                <div className="space-y-16">
                    {menuItems.map((item, index) => (
                        <MenuItem key={index} {...item} />
                    ))}
                </div>
            </div>
        </section>
    );
}
